import React, { useState, useRef } from 'react';
import { shareReportAsPdf, downloadReportAsPdf } from '../../../utils/reportPdfShare';
import { CheckCircle2, ShieldCheck, Share2, Download } from 'lucide-react';

// ── SugarHathi tokens ───────────────────────────────────────────────────
const ORANGE = '#f97316';
const ORANGE_DARK = '#ea580c';
const CREAM = '#fffbf5';
const INK = '#1f2937';
const SLATE = '#6b7280';
const LINE = '#f0e6d8';

const SAMPLE = {
  status: 'SUCCESS',
  kycResult: {
    firstName: 'KRISHNA KUMAR',
    lastName: 'MENA',
    idNumber: 'APPPM4486G',
    dateOfBirth: '05-05-1982',
    mobile: '95XXXXXX24',
    email: 'kr************na@gmail.com',
    aadhaarSeedingStatus: 'Successful',
  },
};

function maskDob(dob) {
  // 05-05-1982 -> XX-XX-1982 (year kept, day/month masked)
  const parts = (dob || '').split('-');
  if (parts.length !== 3) return dob;
  return `XX-XX-${parts[2]}`;
}

function maskPan(pan) {
  // APPPM4486G -> AP******6G (keep first 2 + last 2, mask middle)
  if (!pan || pan.length < 4) return pan;
  return `${pan.slice(0, 2)}${'X'.repeat(pan.length - 4)}${pan.slice(-2)}`;
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: SLATE, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: INK, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-word' }}>
        {value || '\u2014'}
      </div>
    </div>
  );
}

export default function PanDetailedKycCard({ data = SAMPLE }) {
  const cardRef = useRef(null);
  const k = data.kycResult;

  // "Download" triggers the browser's print-to-PDF dialog. "Share" uses the
  // native Web Share API (works on mobile browsers); falls back to print if
  // the browser doesn't support navigator.share. Neither produces a
  // pre-baked PDF file — that needs a server-side renderer if a
  // pixel-perfect branded PDF is required later.
  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadReportAsPdf(cardRef, 'pan-plus-applicant-report.pdf', CREAM); } finally { setPdfBusy(false); }
  };
  const handleShare = async () => {
    const shareText = `PAN Verification — ${k.firstName} ${k.lastName}\nAadhaar Linked: ${k.aadhaarSeedingStatus}`;
    setPdfBusy(true);
    try { await shareReportAsPdf(cardRef, 'pan-plus-applicant-report.pdf', 'PAN Verification — Applicant Report', shareText, CREAM); } finally { setPdfBusy(false); }
  };

  return (
    <div style={{ minHeight: '100%', background: CREAM, padding: '24px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } body { background: #fff !important; } }
      `}</style>
      <div ref={cardRef} style={{ maxWidth: 420, width: '100%', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button
            onClick={handleShare} disabled={pdfBusy}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px',
              color: ORANGE_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Share2 size={13} />
            Share
          </button>
          <button
            onClick={handleDownloadPdf} disabled={pdfBusy}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px',
              color: ORANGE_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Download size={13} />
            Download as PDF
          </button>
        </div>

        <div
          style={{
            background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DARK} 100%)`,
            borderRadius: '18px 18px 0 0', padding: '18px 20px', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <ShieldCheck size={16} color="#fff" strokeWidth={2.5} style={{ flexShrink: 0 }} />
            <span style={{ color: '#fff', fontSize: 12.5, fontWeight: 700, letterSpacing: 0.4 }}>PAN Verified</span>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 10, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
          }}>
            <CheckCircle2 size={12} color="#fff" strokeWidth={3} />
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 800 }}>Aadhaar Linked</span>
          </div>
        </div>

        <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderTop: 'none', borderRadius: '0 0 18px 18px', padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '18px 16px' }}>
          <Field label="First Name" value={k.firstName} />
          <Field label="Last Name" value={k.lastName} />
          <Field label="PAN Number" value={maskPan(k.idNumber)} />
          <Field label="Date of Birth" value={maskDob(k.dateOfBirth)} />
          <Field label="Mobile" value={k.mobile} />
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Email" value={k.email} />
          </div>
        </div>
      </div>
    </div>
  );
}
